import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { clientsAPI, formsAPI, signaturesAPI } from '../lib/api';
import { formatFieldValue, validateFieldValue, getFieldType, US_STATES } from '../lib/validation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon, Loader2, PenTool, X, RotateCcw, Check, FileText,
  ChevronRight, ChevronLeft, Shield, ClipboardList, Users,
  ArrowRightLeft, CheckCircle, AlertCircle, SkipForward, Eye
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

const formIcons = {
  medical_release: Shield,
  screening_form: ClipboardList,
  screening_sheet: ClipboardList,
  specialist_form: Users,
  pcst_authorization: FileText,
  client_transfer: ArrowRightLeft,
  acknowledgment: CheckCircle,
};

const formDescriptions = {
  medical_release: "Authorize release of medical information to Avida Personal Care",
  screening_sheet: "Client screening and contact information",
  specialist_form: "Specialist provider details (optional)",
  pcst_authorization: "Personal Care Service Transfer authorization",
  client_transfer: "Transfer request from previous provider",
  acknowledgment: "Emergency contacts and communication preferences",
};

// Fields that should auto-fill across all forms
const SHARED_FIELDS = {
  // Client name variations
  'last_name': ['last_name'],
  'first_name': ['first_name', 'first_name_mi'],
  'first_name_mi': ['first_name', 'first_name_mi'],
  'client_name': ['full_name'],
  // Date of birth
  'dob': ['dob'],
  'date_of_birth': ['dob'],
  // Address fields
  'address': ['address'],
  'city': ['city'],
  'state': ['state'],
  'state_zip': ['state_zip'],
  'zip': ['zip_code'],
  // Contact
  'phone': ['phone'],
  'email': ['email'],
  // IDs
  'ssn': ['ssn'],
  'medicaid': ['medicaid_id'],
  'medicaid_id': ['medicaid_id'],
  // Dates - auto-fill with today
  'date': ['today'],
  'date_of_visit': ['today'],
  'visit_date': ['today'],
  'client_sig_date': ['today'],
  'rep_sig_date': ['today'],
  'witness_sig_date': ['today'],
  'requested_start_date': ['today'],
};

export default function PacketWizardPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [sharedData, setSharedData] = useState({});
  const [signatures, setSignatures] = useState({});
  const [skippedForms, setSkippedForms] = useState({});
  const [activeSignature, setActiveSignature] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  
  const signatureRef = useRef(null);
  const totalSteps = templates.length + 1; // +1 for review page

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [clientData, templatesData] = await Promise.all([
        clientsAPI.getById(clientId),
        formsAPI.getTemplates(),
      ]);
      
      setClient(clientData);
      setTemplates(templatesData);
      
      // Load saved signatures
      let savedSigs = {};
      try {
        savedSigs = await signaturesAPI.getAll(clientId);
      } catch (e) {
        console.error('Failed to load signatures:', e);
      }
      if (savedSigs && Object.keys(savedSigs).length > 0) {
        setSignatures(savedSigs);
      }

      // Initialize shared data from client
      const today = format(new Date(), 'yyyy-MM-dd');
      const shared = {
        last_name: clientData.last_name || '',
        first_name: clientData.first_name || '',
        full_name: `${clientData.first_name} ${clientData.last_name}`,
        dob: clientData.dob || '',
        address: clientData.address || '',
        city: clientData.city || '',
        state: clientData.state || '',
        state_zip: `${clientData.state} ${clientData.zip_code}`,
        zip_code: clientData.zip_code || '',
        phone: clientData.phone || '',
        email: clientData.email || '',
        ssn: clientData.ssn || '',
        medicaid_id: clientData.medicaid_id || '',
        today: today,
      };
      setSharedData(shared);

      // Pre-fill form data
      const prefilled = {};
      templatesData.forEach(template => {
        template.fields.forEach(field => {
          const fieldKey = `${template.id}.${field.name}`;
          const value = getSharedValue(field.name, shared, clientData);
          if (value) {
            prefilled[fieldKey] = value;
          }
        });
        
        // Auto-select some checkboxes
        if (template.id === 'medical_release') {
          prefilled[`${template.id}.release_all_phi`] = true;
          prefilled[`${template.id}.no_restrictions`] = true;
          prefilled[`${template.id}.release_progress_notes`] = true;  // Default check Progress Notes
          prefilled[`${template.id}.release_history_physical`] = true;  // Default check History & Physical
          prefilled[`${template.id}.client_sig_date`] = today;  // Auto-fill signature date
        }
        
        // Auto-select Client Transfer checkboxes (these are standard acknowledgments)
        if (template.id === 'client_transfer') {
          prefilled[`${template.id}.no_money_offered`] = true;
          prefilled[`${template.id}.not_threatened`] = true;
          prefilled[`${template.id}.understand_coordination`] = true;
        }
        
        // Auto-select PCST: immediately upon receipt + today's date (editable)
        if (template.id === 'pcst_authorization') {
          prefilled[`${template.id}.release_immediately`] = true;
          prefilled[`${template.id}.release_date`] = today;
          prefilled[`${template.id}.printed_name`] = shared.full_name;
        }
        
        // Auto-select Acknowledgment communication preferences
        if (template.id === 'acknowledgment') {
          prefilled[`${template.id}.home_detailed_msg`] = true;
          prefilled[`${template.id}.cell_detailed_msg`] = true;
          prefilled[`${template.id}.mail_to_home`] = true;
        }
      });
      
      setFormData(prefilled);
    } catch (error) {
      toast.error('Failed to load packet');
      navigate('/clients');
    } finally {
      setIsLoading(false);
    }
  };

  const getSharedValue = (fieldName, shared, clientData) => {
    // Check if this field should get a shared value
    const normalizedName = fieldName.toLowerCase();
    
    if (SHARED_FIELDS[normalizedName]) {
      const sourceKeys = SHARED_FIELDS[normalizedName];
      for (const key of sourceKeys) {
        if (shared[key]) return shared[key];
      }
    }
    
    // Check prefill from template field definition
    if (clientData) {
      if (normalizedName.includes('name') && normalizedName.includes('client')) {
        return shared.full_name;
      }
      if (normalizedName === 'first_name_mi') {
        return clientData.first_name;
      }
    }
    
    return '';
  };

  const handleFieldChange = (templateId, fieldName, value) => {
    const fieldKey = `${templateId}.${fieldName}`;
    const formattedValue = formatFieldValue(fieldName, value);
    
    setFormData(prev => ({ ...prev, [fieldKey]: formattedValue }));
    
    // If this is a shared field, update across all forms
    const normalizedName = fieldName.toLowerCase();
    if (SHARED_FIELDS[normalizedName]) {
      const updatedShared = { ...sharedData };
      SHARED_FIELDS[normalizedName].forEach(key => {
        if (key !== 'today') {
          updatedShared[key] = formattedValue;
        }
      });
      setSharedData(updatedShared);
      
      // Update all forms with this shared field
      setFormData(prev => {
        const updated = { ...prev };
        templates.forEach(t => {
          t.fields.forEach(f => {
            const fKey = `${t.id}.${f.name}`;
            const fNorm = f.name.toLowerCase();
            if (SHARED_FIELDS[fNorm] && SHARED_FIELDS[normalizedName].some(k => SHARED_FIELDS[fNorm].includes(k))) {
              updated[fKey] = formattedValue;
            }
          });
        });
        return updated;
      });
    }
    
    // Validate
    const validation = validateFieldValue(fieldName, formattedValue);
    setFieldErrors(prev => {
      if (validation.valid) {
        const { [fieldKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [fieldKey]: validation.message };
    });
  };

  // Auto-focus to next empty field when Enter is pressed or field is complete
  const focusNextEmptyField = (currentFieldKey) => {
    if (!currentTemplate) return;
    
    const allFields = currentTemplate.fields || [];
    const currentIndex = allFields.findIndex(f => `${currentTemplate.id}.${f.name}` === currentFieldKey);
    
    // Find next empty field
    for (let i = currentIndex + 1; i < allFields.length; i++) {
      const field = allFields[i];
      const fieldKey = `${currentTemplate.id}.${field.name}`;
      const value = formData[fieldKey];
      
      // Skip checkboxes and already filled fields
      if (field.type === 'checkbox') continue;
      if (value && value.toString().trim()) continue;
      
      // Focus this field
      const element = document.querySelector(`[data-testid="field-${fieldKey}"]`);
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
  };

  const handleKeyDown = (e, templateId, fieldName) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      focusNextEmptyField(`${templateId}.${fieldName}`);
    }
  };

  const handleDateChange = (templateId, fieldName, date) => {
    const fieldKey = `${templateId}.${fieldName}`;
    const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
    setFormData(prev => ({ ...prev, [fieldKey]: dateStr }));
    
    // If this is a date field, sync to all date fields
    const normalizedName = fieldName.toLowerCase();
    if (normalizedName.includes('date') && normalizedName !== 'dob' && normalizedName !== 'date_of_birth') {
      setFormData(prev => {
        const updated = { ...prev };
        templates.forEach(t => {
          t.fields.forEach(f => {
            const fNorm = f.name.toLowerCase();
            if (fNorm.includes('date') && fNorm !== 'dob' && fNorm !== 'date_of_birth') {
              updated[`${t.id}.${f.name}`] = dateStr;
            }
          });
        });
        return updated;
      });
    }
  };

  const handleCheckboxChange = (templateId, fieldName, checked) => {
    const fieldKey = `${templateId}.${fieldName}`;
    setFormData(prev => ({ ...prev, [fieldKey]: checked }));
  };

  const skipCurrentForm = () => {
    const template = templates[currentStep];
    setSkippedForms(prev => ({ ...prev, [template.id]: true }));
    toast.info(`${template.name} skipped`);
    goToNext();
  };

  const unskipForm = (templateId) => {
    setSkippedForms(prev => {
      const { [templateId]: _, ...rest } = prev;
      return rest;
    });
  };

  const goToNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToForm = (index) => {
    setCurrentStep(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openSignatureModal = (sigKey) => setActiveSignature(sigKey);
  
  const closeSignatureModal = () => {
    setActiveSignature(null);
    signatureRef.current?.clear();
  };

  const saveSignature = async () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const sigData = signatureRef.current.toDataURL('image/png');
      setSignatures(prev => ({ ...prev, [activeSignature]: sigData }));
      
      try {
        await signaturesAPI.save(clientId, activeSignature, sigData);
        toast.success('Signature saved');
      } catch (e) {
        toast.error('Failed to save signature to server');
      }
      
      closeSignatureModal();
    } else {
      toast.error('Please sign before saving');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const submissions = [];
      
      for (const template of templates) {
        if (skippedForms[template.id]) continue;
        
        const templateFormData = {};
        const templateSignatures = {};
        
        Object.entries(formData).forEach(([key, value]) => {
          if (key.startsWith(`${template.id}.`)) {
            templateFormData[key.replace(`${template.id}.`, '')] = value;
          }
        });
        
        Object.entries(signatures).forEach(([key, value]) => {
          if (key.startsWith(`${template.id}.`)) {
            templateSignatures[key.replace(`${template.id}.`, '')] = value;
          }
        });
        
        const result = await formsAPI.submit({
          client_id: clientId,
          form_type: template.id,
          form_data: templateFormData,
          signatures: templateSignatures,
        });
        
        submissions.push(result);
      }

      toast.success('Screening packet completed!');
      navigate(`/clients/${clientId}/packet/complete`, {
        state: { submissions, client }
      });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit packet');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="text-center">
          <img src={AVIDA_LOGO} alt="Avida" className="h-16 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  const currentTemplate = currentStep < templates.length ? templates[currentStep] : null;
  const isReviewStep = currentStep === templates.length;
  const IconComponent = currentTemplate ? (formIcons[currentTemplate.id] || FileText) : Eye;

  const renderField = (template, field) => {
    const fieldKey = `${template.id}.${field.name}`;
    const hasError = fieldErrors[fieldKey];
    
    if (field.type === 'checkbox') {
      return (
        <div 
          key={fieldKey}
          className="flex items-center gap-3 p-4 bg-amber-50/50 rounded-xl hover:bg-amber-100/50 transition-all cursor-pointer"
          onClick={() => handleCheckboxChange(template.id, field.name, !formData[fieldKey])}
        >
          <Checkbox
            checked={formData[fieldKey] || false}
            onCheckedChange={(checked) => handleCheckboxChange(template.id, field.name, checked)}
            data-testid={`field-${fieldKey}`}
          />
          <Label className="text-sm text-blue-900 cursor-pointer flex-1">{field.label}</Label>
        </div>
      );
    }

    if (field.type === 'date' || (field.name.toLowerCase().includes('date') && !field.name.toLowerCase().includes('dob') && !field.name.toLowerCase().includes('birth'))) {
      return (
        <div key={fieldKey} className="space-y-2">
          <Label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{field.label}</Label>
          {field.help && <p className="text-xs text-blue-400 -mt-1">{field.help}</p>}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-12 justify-start text-left" data-testid={`field-${fieldKey}`}>
                <CalendarIcon className="mr-3 h-4 w-4 text-blue-500" />
                {formData[fieldKey] ? format(new Date(formData[fieldKey]), 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData[fieldKey] ? new Date(formData[fieldKey]) : undefined}
                onSelect={(date) => handleDateChange(template.id, field.name, date)}
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    const fieldType = getFieldType(field.name);
    
    if (fieldType === 'state') {
      return (
        <div key={fieldKey} className="space-y-2">
          <Label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{field.label}</Label>
          {field.help && <p className="text-xs text-blue-400 -mt-1">{field.help}</p>}
          <Select value={formData[fieldKey] || ''} onValueChange={(v) => handleFieldChange(template.id, field.name, v)}>
            <SelectTrigger className={hasError ? 'border-red-500' : ''} data-testid={`field-${fieldKey}`}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map(state => <SelectItem key={state} value={state}>{state}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasError && <p className="text-xs text-red-500">{hasError}</p>}
        </div>
      );
    }

    // Check if field is auto-filled from shared data
    const isShared = SHARED_FIELDS[field.name.toLowerCase()];
    
    return (
      <div key={fieldKey} className="space-y-2">
        <Label className="text-xs font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-2">
          {field.label}
          {isShared && formData[fieldKey] && (
            <span className="text-green-600 text-[10px] font-normal bg-green-100 px-2 py-0.5 rounded-full">Auto-filled</span>
          )}
        </Label>
        {field.help && <p className="text-xs text-blue-400 -mt-1">{field.help}</p>}
        <Input
          value={formData[fieldKey] || ''}
          onChange={(e) => handleFieldChange(template.id, field.name, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, template.id, field.name)}
          className={`h-12 ${hasError ? 'border-red-500' : ''}`}
          data-testid={`field-${fieldKey}`}
          placeholder={
            fieldType === 'phone' ? '(XXX) XXX-XXXX' :
            fieldType === 'ssn' ? 'XXX-XX-XXXX' :
            fieldType === 'zip' ? 'XXXXX' : ''
          }
        />
        {hasError && <p className="text-xs text-red-500">{hasError}</p>}
      </div>
    );
  };

  const renderSignatures = (template) => {
    if (!template.signatures?.length) return null;
    
    return (
      <div className="mt-6 pt-6 border-t border-amber-200">
        <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider flex items-center gap-2 mb-4">
          <PenTool className="w-4 h-4" /> Signatures Required
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {template.signatures.map((sig) => {
            const sigKey = `${template.id}.${sig.name}`;
            const hasSignature = Boolean(signatures[sigKey]);
            return (
              <div key={sigKey}>
                <Label className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 block">
                  {sig.label}
                  {hasSignature && <span className="ml-2 text-green-600"><CheckCircle className="w-3 h-3 inline" /> Saved</span>}
                </Label>
                {hasSignature ? (
                  <div 
                    className="border-2 border-green-400 rounded-xl p-4 bg-green-50 cursor-pointer h-24 flex items-center justify-center"
                    onClick={() => openSignatureModal(sigKey)}
                    data-testid={`signature-preview-${sigKey}`}
                  >
                    <img src={signatures[sigKey]} alt="Signature" className="max-h-16" />
                  </div>
                ) : (
                  <button
                    className="w-full h-24 border-2 border-dashed border-amber-300 rounded-xl flex items-center justify-center gap-3 hover:bg-amber-50 transition-all"
                    onClick={() => openSignatureModal(sigKey)}
                    data-testid={`signature-button-${sigKey}`}
                  >
                    <PenTool className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">Tap to Sign</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderReviewPage = () => {
    const formsToSubmit = templates.filter(t => !skippedForms[t.id]);
    const skippedList = templates.filter(t => skippedForms[t.id]);
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-blue-900">Review Your Packet</h2>
          <p className="text-blue-600 mt-2">Review all forms before final submission</p>
        </div>

        {/* Forms to Submit */}
        <div className="space-y-3">
          <h3 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Forms to Submit ({formsToSubmit.length})</h3>
          {formsToSubmit.map((template, idx) => {
            const Icon = formIcons[template.id] || FileText;
            const filledFields = template.fields.filter(f => formData[`${template.id}.${f.name}`]).length;
            const totalFields = template.fields.length;
            const hasSigs = template.signatures?.every(s => signatures[`${template.id}.${s.name}`]);
            
            return (
              <Card key={template.id} className="p-4 hover:shadow-md transition-all cursor-pointer" onClick={() => goToForm(idx)}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">{template.name}</h4>
                    <p className="text-xs text-blue-500">{filledFields}/{totalFields} fields filled</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasSigs !== false && template.signatures?.length > 0 && (
                      <span className="text-green-600"><CheckCircle className="w-5 h-5" /></span>
                    )}
                    <ChevronRight className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Skipped Forms */}
        {skippedList.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-amber-700 text-sm uppercase tracking-wide">Skipped Forms ({skippedList.length})</h3>
            {skippedList.map((template) => {
              const Icon = formIcons[template.id] || FileText;
              const idx = templates.findIndex(t => t.id === template.id);
              return (
                <Card key={template.id} className="p-4 bg-amber-50/50 border-amber-200">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-200 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-800">{template.name}</h4>
                      <p className="text-xs text-amber-600">This form will not be submitted</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); unskipForm(template.id); goToForm(idx); }}
                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    >
                      Include
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Client Summary */}
        <Card className="p-5 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3">Client Information</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-blue-500">Name:</span> <span className="font-medium text-blue-900">{sharedData.full_name}</span></div>
            <div><span className="text-blue-500">DOB:</span> <span className="font-medium text-blue-900">{sharedData.dob}</span></div>
            <div><span className="text-blue-500">Phone:</span> <span className="font-medium text-blue-900">{sharedData.phone}</span></div>
            <div><span className="text-blue-500">Address:</span> <span className="font-medium text-blue-900">{sharedData.address}, {sharedData.city}</span></div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-amber-50 pb-32">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md sticky top-0 z-20 shadow-sm border-b border-amber-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
            <div className="flex-1">
              <h1 className="font-bold text-lg text-blue-900">{client?.first_name} {client?.last_name}</h1>
              <p className="text-xs text-blue-500">Screening Packet</p>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-2">
            {templates.map((t, i) => {
              const Icon = formIcons[t.id] || FileText;
              const isActive = i === currentStep;
              const isCompleted = i < currentStep;
              const isSkipped = skippedForms[t.id];
              
              return (
                <button
                  key={t.id}
                  onClick={() => goToForm(i)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                    isActive ? 'bg-blue-600 text-white' :
                    isSkipped ? 'bg-amber-100 text-amber-600' :
                    isCompleted ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  data-testid={`step-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{t.name.split(' ')[0]}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
              );
            })}
            <button
              onClick={() => setCurrentStep(templates.length)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                isReviewStep ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              data-testid="step-review"
            >
              <Eye className="w-4 h-4" />
              <span>Review</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {isReviewStep ? (
          renderReviewPage()
        ) : currentTemplate && (
          <div className="space-y-6">
            {/* Form Header */}
            <div className="text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                skippedForms[currentTemplate.id] ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                <IconComponent className={`w-8 h-8 ${skippedForms[currentTemplate.id] ? 'text-amber-600' : 'text-blue-600'}`} />
              </div>
              <h2 className="text-2xl font-bold text-blue-900">{currentTemplate.name}</h2>
              <p className="text-blue-500 mt-1">{formDescriptions[currentTemplate.id] || currentTemplate.description}</p>
              <p className="text-sm text-blue-400 mt-2">Step {currentStep + 1} of {totalSteps}</p>
            </div>

            {skippedForms[currentTemplate.id] ? (
              <Card className="p-8 text-center bg-amber-50 border-amber-200">
                <SkipForward className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="font-semibold text-amber-800 text-lg">This form is skipped</h3>
                <p className="text-amber-600 mt-2">This form will not be included in the final packet.</p>
                <Button onClick={() => unskipForm(currentTemplate.id)} className="mt-4" variant="outline">
                  Include This Form
                </Button>
              </Card>
            ) : (
              <Card className="p-6 bg-white shadow-lg border-0">
                {/* Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentTemplate.fields.map(field => renderField(currentTemplate, field))}
                </div>
                
                {/* Checkboxes in separate section */}
                {currentTemplate.checkboxes?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-amber-100">
                    <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-4">Options</h3>
                    <div className="space-y-2">
                      {currentTemplate.checkboxes.map(cb => renderField(currentTemplate, { ...cb, type: 'checkbox' }))}
                    </div>
                  </div>
                )}
                
                {/* Signatures */}
                {renderSignatures(currentTemplate)}
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-amber-200 p-4 shadow-lg">
        <div className="max-w-3xl mx-auto flex gap-3">
          {currentStep > 0 && (
            <Button variant="outline" onClick={goToPrev} className="h-14 px-6" data-testid="prev-button">
              <ChevronLeft className="w-5 h-5 mr-1" /> Back
            </Button>
          )}
          
          {!isReviewStep && currentTemplate && !skippedForms[currentTemplate.id] && (
            <Button 
              variant="ghost" 
              onClick={skipCurrentForm} 
              className="h-14 px-4 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              data-testid="skip-button"
            >
              <SkipForward className="w-5 h-5 mr-1" /> Skip
            </Button>
          )}
          
          <div className="flex-1" />
          
          {isReviewStep ? (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className="h-14 px-8 bg-green-600 hover:bg-green-700"
              data-testid="submit-button"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating PDFs...</>
              ) : (
                <><Check className="w-5 h-5 mr-2" /> Submit Packet</>
              )}
            </Button>
          ) : (
            <Button onClick={goToNext} className="h-14 px-8" data-testid="next-button">
              {currentStep === templates.length - 1 ? 'Review' : 'Next'} <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Signature Modal */}
      {activeSignature && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 bg-amber-50 border-b border-amber-100">
              <h3 className="font-bold text-lg text-blue-900">Sign Here</h3>
              <Button variant="ghost" size="icon" onClick={closeSignatureModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4">
              <div className="border-2 border-dashed border-amber-300 rounded-xl bg-amber-50/50 h-48 relative">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{ className: 'w-full h-full', style: { width: '100%', height: '100%' } }}
                  penColor="#1E3A8A"
                />
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-amber-100">
              <Button variant="outline" onClick={() => signatureRef.current?.clear()} className="flex-1 h-12">
                <RotateCcw className="w-4 h-4 mr-2" /> Clear
              </Button>
              <Button onClick={saveSignature} className="flex-1 h-12 bg-blue-600 hover:bg-blue-700">
                <Check className="w-4 h-4 mr-2" /> Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
